import { useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import { PillViewType } from '../../types';
import { RemovablePill, SortablePill } from './components';

type PillListPropsType = {
  pills: PillViewType[];
  isDragDisabled: boolean;
  onRemovePillAtIndex: (index: number) => void;
  onPillClick: (index: number) => void;
  onReorderPills: (fromIndex: number, toIndex: number) => void;
};

export const PillList: React.FC<PillListPropsType> = ({
  pills,
  isDragDisabled,
  onRemovePillAtIndex,
  onPillClick,
  onReorderPills,
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );
  const sortableIds = pills.map((pill) => pill.id);
  const activeIndex = activeId === null ? null : sortableIds.indexOf(activeId);
  const activePill = activeIndex === null ? null : pills[activeIndex];

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id.toString());
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);

    if (!over || active.id === over.id) {
      return;
    }

    onReorderPills(
      sortableIds.indexOf(active.id.toString()),
      sortableIds.indexOf(over.id.toString())
    );
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext
        items={sortableIds}
        strategy={horizontalListSortingStrategy}
      >
        {pills.map((pill, index) => (
          <SortablePill
            key={pill.id}
            pill={pill}
            sortableId={pill.id}
            isDragDisabled={isDragDisabled}
            onRemove={() => onRemovePillAtIndex(index)}
            onClick={pill.isClickable ? () => onPillClick(index) : undefined}
          />
        ))}
      </SortableContext>

      <DragOverlay>
        {activePill && activeIndex !== null ? (
          <RemovablePill
            label={activePill.label}
            color={activePill.color}
            isActive={activePill.isActive}
            onRemove={() => onRemovePillAtIndex(activeIndex)}
            onClick={
              activePill.isClickable
                ? () => onPillClick(activeIndex)
                : undefined
            }
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
